import type { User, Account } from '@/common/types'
import { getAccountConfirmationRedirect, isAccountConfirmationRequired } from './getAccountConfirmationRedirect'
import { getOnboardingRedirect } from './getOnboardingRedirect'

/**
 * Resolves the single navigation guard redirect AppContainer should apply, in priority order:
 *
 * 1. Account confirmation - if the active account needs the participant to confirm their details
 * 2. Onboarding - user profile / account setup steps, skipped entirely while confirmation is
 *    outstanding so a participant isn't bounced into onboarding before confirming their account
 *
 * Pure function for easy testing and memoization.
 *
 * @returns Route to redirect to, or null if no redirect needed
 */
export function getAppContainerRedirect(params: {
    user: User | null
    account: Account | null
    accounts: Account[]
    primaryAccount: Account | null
    isAccountSwitching: boolean
    isAccountsLoaded: boolean
    pathname: string
}): string | null {
    const { user, account, accounts, primaryAccount, isAccountSwitching, isAccountsLoaded, pathname } = params

    const confirmationRedirect = getAccountConfirmationRedirect(user, account, isAccountSwitching, pathname)
    if (confirmationRedirect) return confirmationRedirect

    if (isAccountConfirmationRequired(account)) return null

    // Don't redirect to onboarding while accounts are still loading (e.g. right after a hard
    // refresh) - resolving against a partial accounts list could send the user to account-type
    // selection before their real accounts have arrived.
    if (!isAccountsLoaded) return null

    return getOnboardingRedirect(user, isAccountSwitching, accounts, primaryAccount, account)
}
